import * as css from 'css/exports.module.scss';

import './SearchItem.scss';

import { ActiveIndicator } from '@ui/icons';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { VersionNumber } from '@ui/VersionNumber';

import { EmailTemplateStatus, RuleStatus, SonarObjectsSearchItem } from '@sonar-software/sonar-core';
import React, { useCallback } from 'react';
import { shouldItemShowApiName, shouldItemShowParent } from './arch/lib';
import { Space, Tooltip } from 'antd';

const delimiter = '.';

function getItemDisplayName(item: SonarObjectsSearchItem, type: SonarObjectType) {
	const { displayName, parentDisplayName, internalName } = item;

	if (shouldItemShowApiName(type)) {
		return (
			<Tooltip
				mouseEnterDelay={ 0.5 }
				open={ displayName.length + internalName.length > 33 ? undefined : false }
				placement='right'
				title={ internalName }>
				<Space size={ Number(css.size8) }>
					<span className='display'>{ displayName }</span>
					<span className='secondary-text'>{ internalName }</span>
				</Space>
			</Tooltip>
		);
	}

	if (shouldItemShowParent(type)) {
		return (
			<>
				<span className='secondary-text'>{ parentDisplayName }</span>
				<span className='dot'>{ delimiter }</span>
				<span className='display'>{ displayName }</span>
			</>
		);
	}

	return <span className='display'>{ displayName }</span>;
}

function getActiveIndicator(status: RuleStatus | EmailTemplateStatus | undefined) {
	if (status === undefined) {
		return undefined;
	}

	return status === RuleStatus.Active || status === EmailTemplateStatus.Published;
}

export interface SearchItemProps {
	item: SonarObjectsSearchItem;
	onClick?: (item: SonarObjectsSearchItem, type: SonarObjectType) => void;
	type: SonarObjectType;
}

export const SearchItem: React.FC<SearchItemProps> = ({ item, onClick, type }) => {
	const status = item?.status;

	const handleClick = useCallback(() => {
		if (onClick) {
			onClick(item, type);
		}
	}, [ item, onClick, type ]);

	return (
		<div
			className='search-item'
			onClick={ handleClick }>
			<span className='name'>
				{ getItemDisplayName(item, type) }
				<VersionNumber number={ item.versionNumber } />
			</span>
			<ActiveIndicator isActive={ getActiveIndicator(status as RuleStatus | EmailTemplateStatus) } />
		</div>
	);
};
